import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Boxes, Box, Hammer, Headphones, MapPin, Package, Plus, ShieldCheck, Truck, UserPlus } from 'lucide-react';
import type { AssetCategory } from '@/api/types';
import { useAuth } from '@/auth/AuthProvider';
import { PERMISSIONS } from '@/auth/permissions';
import { Menu, MenuItem, MenuLabel } from '@/design-system/Overlay';
import { CreateAssetDialog } from '@/features/assets/CreateAssetDialog';
import { CreateConsumableDialog } from '@/features/consumables/CreateConsumableDialog';
import { ContractDialog } from '@/features/contracts/ContractDialogs';
import { WorkOrderDialog } from '@/features/maintenance/WorkOrderDialogs';
import { CreateEmployeeDialog } from '@/features/employees/CreateEmployeeDialog';
import { VendorDialog } from '@/features/settings/CatalogSection';
import { LocationDialog } from '@/features/settings/OrganizationSection';

type Kind =
  | 'asset'
  | 'accessory'
  | 'general'
  | 'consumable'
  | 'workOrder'
  | 'contract'
  | 'user'
  | 'location'
  | 'vendor';

const ASSET_CATEGORY: Partial<Record<Kind, AssetCategory>> = {
  accessory: 'PERIPHERAL',
  general: 'OTHER',
};

/**
 * Create, from anywhere.
 *
 * Each entry opens its form right here, over whatever page you are on, instead of navigating to the
 * owning screen first. Once saved, a record with a page of its own (an asset, a person) opens; the
 * rest stay put with a confirmation.
 *
 * Entries are gated on the permission the save needs, so the menu never offers a form the server
 * would refuse.
 */
export function CreateMenu({ className, label = 'Create' }: { className?: string; label?: string }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState<Kind | null>(null);
  const close = () => setOpen(null);

  const entries: {
    kind: Kind;
    label: string;
    description: string;
    icon: ReactNode;
    allowed: boolean;
  }[] = [
    {
      kind: 'asset',
      label: 'Asset',
      description: 'Register a device in the fleet',
      icon: <Boxes />,
      allowed: can(PERMISSIONS.ASSET_WRITE),
    },
    {
      kind: 'accessory',
      label: 'Accessory',
      description: 'Keyboard, mouse, dock, headset…',
      icon: <Headphones />,
      allowed: can(PERMISSIONS.ASSET_WRITE),
    },
    {
      kind: 'general',
      label: 'General item',
      description: 'Anything else worth tracking',
      icon: <Box />,
      allowed: can(PERMISSIONS.ASSET_WRITE),
    },
    {
      kind: 'consumable',
      label: 'Consumable',
      description: 'A stocked item counted by quantity',
      icon: <Package />,
      allowed: can(PERMISSIONS.CONSUMABLE_WRITE),
    },
    {
      kind: 'workOrder',
      label: 'Work order',
      description: 'A repair or servicing job on an asset',
      icon: <Hammer />,
      allowed: can(PERMISSIONS.MAINTENANCE_WRITE),
    },
    {
      kind: 'contract',
      label: 'Warranty / AMC',
      description: 'A contract covering assets',
      icon: <ShieldCheck />,
      allowed: can(PERMISSIONS.CONTRACT_WRITE),
    },
    {
      kind: 'user',
      label: 'User',
      description: 'Add someone to the register',
      icon: <UserPlus />,
      allowed: can(PERMISSIONS.EMPLOYEE_WRITE),
    },
    {
      kind: 'location',
      label: 'Location',
      description: 'An office, warehouse or site',
      icon: <MapPin />,
      allowed: can(PERMISSIONS.ORG_WRITE),
    },
    {
      kind: 'vendor',
      label: 'Vendor',
      description: 'Who you buy from',
      icon: <Truck />,
      allowed: can(PERMISSIONS.CATALOG_WRITE),
    },
  ];
  const allowed = entries.filter((entry) => entry.allowed);
  if (allowed.length === 0) return null;

  return (
    <>
      <Menu
        label="Create something new"
        className={className}
        trigger={
          <>
            <Plus />
            <span className="hidden sm:inline">{label}</span>
          </>
        }
      >
        {(closeMenu) => (
          <>
            <MenuLabel>Create new</MenuLabel>
            {allowed.map((entry) => (
              <MenuItem
                key={entry.kind}
                icon={entry.icon}
                description={entry.description}
                onClick={() => {
                  closeMenu();
                  setOpen(entry.kind);
                }}
              >
                {entry.label}
              </MenuItem>
            ))}
          </>
        )}
      </Menu>

      <CreateAssetDialog
        open={open === 'asset' || open === 'accessory' || open === 'general'}
        category={open ? ASSET_CATEGORY[open] : undefined}
        onClose={close}
        onCreated={(asset) => {
          close();
          navigate(`/assets/${asset.id}`);
        }}
      />
      <CreateConsumableDialog open={open === 'consumable'} onClose={close} onCreated={close} />
      {open === 'workOrder' && (
        <WorkOrderDialog
          open
          onClose={close}
          onSaved={(order) => navigate(`/maintenance/work-orders/${order.id}`)}
        />
      )}
      {open === 'contract' && (
        <ContractDialog onClose={close} onSaved={(contract) => navigate(`/contracts/${contract.id}`)} />
      )}
      <CreateEmployeeDialog
        open={open === 'user'}
        onClose={close}
        onCreated={(employee) => {
          close();
          navigate(`/employees/${employee.id}`);
        }}
      />
      <LocationDialog value={open === 'location' ? 'new' : null} onClose={close} />
      <VendorDialog value={open === 'vendor' ? 'new' : null} onClose={close} />
    </>
  );
}
